'use client';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { PurchaseJourneyConstants } from '@/lib/purchaseJourney/purchaseJourneyConstants';
import {
  // Core cart state
  cartStateAtom,
  cartItemsAtom,
  cartTotalsAtom,
  cartOperationsAtom,
  isCartSlideOutOpenAtom,
  cartItemsQuantityAtom,

  // Operation loaders
  isRemovingOneItemAtom,
  isAddingOneAtom,
  isRemovingLineItemAtom,

  // External integrations
  cartIdWithStorageAtom,
  mixpanelUserIdentityAtom,
  cartStateWithProductsAtom,
  productStoreAtom,
} from '@/store/cartStore';

import {
  mixpanelRemoveFromCartEvent,
  mixpanelAddToCartEvent,
} from '@/lib/mixpanel/services/client';
import { useStatsigClient } from '@statsig/react-bindings';
import {
  getServerCart,
  createServerCart,
  updateServerCart,
} from '@/actions/cartActions';
import {
  CartContactType,
  CartItemType,
  CartStateType,
  CartTotalsType,
  ProductType,
  SalesCartModelPayload,
} from '@/types/types';
import {
  SalesCartModel,
  SalesChannel,
  SalesFulfilmentMode,
} from '@ikhokha/sales-types';
import { getProduct } from '@/sanity/services/productService';
import { useMemo, useState } from 'react';
import {
  fetchOrCreateServerContact,
  generateServerPaymentUrl,
} from '../actions/cartActions';

export interface DiscountResult {
  success: boolean;
  message: string;
}

export interface CheckoutData {
  id: string | null;
  lineItems: CartItemType[];
  totals: CartTotalsType;
  discountCode: string;
}

export interface UseCartReturn {
  // Core cart state
  cart: CartStateType;
  items: CartItemType[];
  totals: CartTotalsType;
  itemCount: number;
  isEmpty: boolean;
  isCartOpen: boolean;

  // Core operations
  addItem: (product: ProductType | ProductType[], quantity?: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  incrementQuantity: (itemId: string) => void;
  decrementQuantity: (itemId: string) => void;
  applyDiscount: (code: string) => Promise<DiscountResult>;
  clearDiscount: () => Promise<void>;
  setDiscount: (code: string) => void;
  clearCart: () => void;
  setCart: (cart: CartStateType) => void;
  getCart: (cartId?: string) => Promise<CartStateType | null>;
  updateContact: (contact: CartContactType) => void;
  updateDeliveryAddress: (address: CartStateType['deliveryAddress']) => void;
  updateBillingAddress: (address: CartStateType['billingAddress']) => void;
  updateBillingSameAsDelivery: (same: boolean) => void;
  // Cart UI operations
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;

  // Loading states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isRemovingItem: boolean | string | number;
  setIsRemovingItem: (removing: boolean | string | number) => void;
  isAddingItem: boolean | string | number | string[];
  setIsAddingItem: (adding: boolean | string | number | string[]) => void;
  isRemovingLineItem: boolean | string | number;
  setIsRemovingLineItem: (removing: boolean | string | number) => void;

  // External integrations
  cartId: string | null;
  setCartId: (id: string | null) => void;
  mixpanelUserId: string | null;
  setMixpanelUserId: (id: string | null) => void;

  createCart: () => Promise<CartStateType | undefined>;
  updateCart: () => Promise<CartStateType | undefined>;
  getDebitCard: () => () => Promise<ProductType | null>;
  updateSoleProprietor: (isSoleProprietor: boolean) => void;
  checkout: () => Promise<{
    cart: SalesCartModel;
    paymentUrl: string;
  }>;
  formatAndSetInitialCart: (
    cart: SalesCartModel & { products: { [id: string]: ProductType } },
    source: 'default' | 'ik-card'
  ) => void;
}

// Main Cart Hook
const useCart = (): UseCartReturn => {
  // Get atoms
  // Core cart state
  const cart = useAtomValue(cartStateAtom);
  const computedCart = useAtomValue(cartStateWithProductsAtom);
  const [items] = useAtom(cartItemsAtom);
  const [totals] = useAtom(cartTotalsAtom);
  const [itemCount] = useAtom(cartItemsQuantityAtom);
  const [, dispatch] = useAtom(cartOperationsAtom);
  const [isCartOpen, setIsCartOpen] = useAtom(isCartSlideOutOpenAtom);
  const setProducts = useSetAtom(productStoreAtom);

  const getDebitCard = useMemo(() => {
    let cachedProduct: any = null;

    return () => async (): Promise<ProductType | null> => {
      if (!cachedProduct) {
        cachedProduct = await getProduct(
          PurchaseJourneyConstants.DEBIT_CARD_PRODUCT_ID
        );
      }
      return cachedProduct;
    };
  }, []);

  const debitCard = getDebitCard();
  // Operation loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isRemovingItem, setIsRemovingItem] = useAtom(isRemovingOneItemAtom);
  const [isAddingItem, setIsAddingItem] = useAtom(isAddingOneAtom);
  const [isRemovingLineItem, setIsRemovingLineItem] = useAtom(
    isRemovingLineItemAtom
  );

  // External integrations
  const [cartId, setCartId] = useAtom(cartIdWithStorageAtom);
  const [mixpanelUserId, setMixpanelUserId] = useAtom(mixpanelUserIdentityAtom);

  // Stats client for analytics
  const { logEvent } = useStatsigClient();

  //internals
  const productIsDebitCard = (product: ProductType) => {
    return product?.slug === PurchaseJourneyConstants.DEBIT_CARD_PRODUCT_ID;
  };

  // Add item to cart
  const addItem = (
    products: ProductType | ProductType[],
    quantity = 1
  ): void => {
    const productArray = Array.isArray(products) ? products : [products];

    productArray.forEach((product) => {
      setIsAddingItem(product.id);

      dispatch({
        type: 'ADD_ITEM',
        payload: {
          product,
          quantity,
          lockQuantity: productIsDebitCard(product),
          hideLineItem: productIsDebitCard(product),
        },
      });

      // Track analytics
      mixpanelAddToCartEvent({ product, quantity });
      logEvent('add_to_cart', product.title || '', {
        id: product.id,
        name: product.title || '',
        price: `${product.price}`,
        event_source: 'Online',
        currency_code: 'ZAR',
        category: (product?.categorySlug || '').replace(/-/g, ' '),
      });

      setTimeout(() => setIsAddingItem(false), 300);
    });
  };

  // Remove item from cart
  const removeItem = (itemId: string): void => {
    setIsRemovingLineItem(itemId);

    // Find the item before removing it for analytics
    const item = items.find((item) => item.id === itemId);

    dispatch({
      type: 'REMOVE_ITEM',
      payload: { itemId },
    });

    // Track analytics if item was found
    if (item) {
      mixpanelRemoveFromCartEvent(item);
      logEvent('remove_from_cart', item.product.title || '', {
        id: item.product.id,
        name: item.product.title || '',
        price: `${item.product.price}`,
        event_source: 'Online',
        currency_code: 'ZAR',
        category: (item.product.categorySlug || '').replace(/-/g, ' '),
      });
    }

    // Close cart if empty
    if (items.length <= 1) {
      setIsCartOpen(false);
    }

    setTimeout(() => setIsRemovingLineItem(false), 300);
  };

  // Update item quantity
  const updateQuantity = (itemId: string, quantity: number): void => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    const item = items.find((item) => item.id === itemId);

    // Skip update if item has lockQuantity set
    if (item?.lockQuantity) {
      return;
    }

    const currentQuantity = item?.quantity || 0;

    if (quantity > currentQuantity) {
      setIsAddingItem(itemId);
    } else {
      setIsRemovingItem(itemId);
    }

    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { itemId, quantity },
    });

    setTimeout(() => {
      setIsAddingItem(false);
      setIsRemovingItem(false);
    }, 300);
  };

  // Increment item quantity
  const incrementQuantity = (itemId: string): void => {
    const item = items.find((item) => item.id === itemId);
    if (item && !item.lockQuantity) {
      updateQuantity(itemId, item.quantity + 1);
    }
  };

  // Decrement item quantity
  const decrementQuantity = (itemId: string): void => {
    const item = items.find((item) => item.id === itemId);
    if (item && !item.lockQuantity) {
      if (item.quantity > 1) {
        updateQuantity(itemId, item.quantity - 1);
      } else {
        removeItem(itemId);
      }
    }
  };

  // Apply discount to cart
  const applyDiscount = async (code: string): Promise<DiscountResult> => {
    setIsLoading(true);
    try {
      if (!code) {
        return {
          success: false,
          message: 'Discount code is required',
        };
      }
      if (!cart.id) {
        return {
          success: false,
          message: 'Cart ID is required',
        };
      }

      const { items } = await mapInternalCartToServerCart();

      console.log('Applying discount code:', code);
      const { data, error } = await updateServerCart(cart.id, {
        discount_codes: [code.toUpperCase()],
        items,
      });
      console.log('applyDiscount', data, error);

      if (error) {
        return {
          success: false,
          message: 'Error applying discount code',
        };
      }
      if (!data) {
        return {
          success: false,
          message: 'Invalid discount code',
        };
      }

      const cartData = mapServerCartToInternal(data);

      console.log('Cart data after discount:', cartData);
      dispatch({
        type: 'SET_CART',
        payload: cartData,
      });

      return {
        success: true,
        message: 'Discount applied successfully',
      };
    } catch (error) {
      console.error('Error applying discount code:', error);
      return {
        success: false,
        message: 'Invalid discount code',
      };
    }
  };

  // Clear discount
  const clearDiscount = async (): Promise<void> => {
    if (!cart.id) {
      return;
    }
    const { items } = await mapInternalCartToServerCart();
    const { data, error } = await updateServerCart(cart.id, {
      discount_codes: [],
      items,
    });
    console.log('clearDiscount', data, error);
    if (error) {
      console.error('Error clearing discount code:', error);
      return;
    }
    if (!data) {
      console.error('No data returned from clearing discount code');
      return;
    }
    dispatch({ type: 'CLEAR_DISCOUNT' });
  };

  const setDiscount = (code: string): void => {
    dispatch({
      type: 'SET_DISCOUNT',
      payload: code.toUpperCase(),
    });
  };

  // Clear cart
  const clearCart = (): void => {
    dispatch({ type: 'CLEAR_CART' });
    setIsCartOpen(false);
  };

  // Set entire cart (for API syncing)
  const setCart = (cartData: CartStateType): void => {
    dispatch({
      type: 'SET_CART',
      payload: cartData,
    });
  };

  // Open cart
  const openCart = (): void => {
    setIsCartOpen(true);
  };

  // Close cart
  const closeCart = (): void => {
    setIsCartOpen(false);
  };

  // Toggle cart
  const toggleCart = (): void => {
    setIsCartOpen(!isCartOpen);
  };

  // Update Contact
  const updateContact = (contact: CartContactType): void => {
    dispatch({
      type: 'SET_CONTACT',
      payload: contact,
    });
  };

  // Update Delivery Address
  const updateDeliveryAddress = (
    address: CartStateType['deliveryAddress']
  ): void => {
    dispatch({
      type: 'SET_DELIVERY_ADDRESS',
      payload: address,
    });
  };

  // isSoleProprietor
  const updateSoleProprietor = (isSoleProprietor: boolean): void => {
    dispatch({
      type: 'SET_SOLE_PROPRIETOR',
      payload: isSoleProprietor,
    });
  };

  // Update Billing Address
  const updateBillingAddress = (
    address: CartStateType['billingAddress']
  ): void => {
    dispatch({
      type: 'SET_BILLING_ADDRESS',
      payload: address,
    });
  };

  // billingsameasdelivery
  const updateBillingSameAsDelivery = (same: boolean): void => {
    dispatch({
      type: 'SET_BILLING_SAME_AS_DELIVERY',
      payload: same,
    });
  };

  // Fetch cart from API
  const getCart = async (cartId?: string): Promise<CartStateType | null> => {
    setIsLoading(true);

    try {
      // If cartId is provided, update the stored cart ID
      if (cartId) {
        setCartId(cartId);
      }

      // Use the provided cartId or the one in state
      const currentCartId = cartId || cartId;

      if (!currentCartId) {
        setIsLoading(false);
        return null;
      }

      // Fetch the cart data from your API
      const { data: retrievedCart } = await getServerCart(currentCartId);

      console.log('retrievedCart', retrievedCart);
      if (!retrievedCart) {
        setIsLoading(false);
        return null;
      }

      //   {
      //     "id": "6817cad36424080012172bea",
      //     "sales_channel": "self-service",
      //     "payment_ref": "TX8CDGCQ",
      //     "delivery": 0,
      //     "items_discount": 0,
      //     "discount_total": 0,
      //     "delivery_discount": 0,
      //     "vat": 52.1739,
      //     "total": 400,
      //     "discount_codes": [],
      //     "bundle_savings_total": 10599,
      //     "sub_total": 400,
      //     "items": [
      //         {
      //             "id": "67ea569798544c00129308b6",
      //             "name": "Bundle test",
      //             "parent_name": null,
      //             "quantity": 1,
      //             "price": 400,
      //             "images": [
      //                 "https://cdn.sanity.io/images/2xvhb43x/staging/d50c2b66d8bdcd2b16a282b0ce9111f13ccf2735-412x379.jpg"
      //             ],
      //             "sku": "SaleOnAThingSku",
      //             "parent_id": null,
      //             "discount_total": 0,
      //             "bundle_details": {
      //                 "bundle_items": [
      //                     {
      //                         "name": "SIM + BANK CARD",
      //                         "parent_name": "iK Flyer Lite",
      //                         "sku": "SCKLT6810DC7",
      //                         "images": [
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e45f4a71900110f0f62/f5efa3e24b2d0c880b416cbdec9698a9/iK-Flyer-Lite_Digital_Renders_1_f2161d42-701f-4a1c-a037-f2a12b7180e8-removebg-preview.png%253Fv%253D1714389802",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e46dd1f1f0011b7f980/59d783ae58d2ed3c3d9057e1693451cc/iK-Flyer-Lite_Digital_Renders_2_f1aa04ff-7482-46b1-9345-ca63e3008eca.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e45f3a47b0011e4f5d3/5855ee5dc0403f4b52d8de4c2b9d6e69/iK-Flyer-Lite_Digital_Renders_1_f2161d42-701f-4a1c-a037-f2a12b7180e8.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e46a0569b0012117099/7158fde00ef28658bb73b9f096c654b9/iK-Flyer-Lite_Digital_Renders_5_0ba03057-d10f-43e9-82c4-e1231331319c.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e459257fe0012d270cc/2e5974ec606091a102c240677db84f07/PerspecLeft1_1.png%253Fv%253D1714389802",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e4667b1170011f6d9ed/15db6c462f741d5056353b3eb9ca6793/iK-Flyer-Lite_Digital_Renders_4_093dbff1-979c-4941-9fb6-96f602e1bffb.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e468ff45e0012c9e683/d2b8e01d8ad0adb92c018bbf87d8f505/iK-Flyer-Lite_Digital_Renders_6_7cd96b4d-079f-48be-a46e-e77be1ed8fba.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e46cd82ee001212e08f/f5fdd4b2d7e2e1bea7ea4b9bc995857f/iK-Flyer-Lite_Digital_Renders_8_69c96cfc-0527-45bb-a1cf-6b9ed2037261.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e46f3a47b0011e4f5d6/9dc003b6d1f9d28d99c401ff9fe65611/iK-Flyer-Lite_Digital_Renders_3_b562ca0d-0857-4586-8a3f-9fabcb453ecc.png%253Fv%253D1714388636",
      //                             "https://cdn.swell.store/ikhokha_test/67bf0e465cf1bc0012877ee4/b0bcb853e62f9a60260f99be3e5d86b9/iK-Flyer-Lite_Digital_Renders_7_6ec023e7-b48a-423d-9595-3597d08d81a6.png%253Fv%253D1714388636"
      //                         ],
      //                         "quantity": 2,
      //                         "original_price": 500,
      //                         "cart_metadata_fields": [
      //                             "serial_numbers"
      //                         ]
      //                     },
      //                     {
      //                         "name": "iK POS",
      //                         "parent_name": null,
      //                         "sku": "SCT2MINIL1323",
      //                         "images": [
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6e2843240011d691b0/d7ff314272691473f61cfb13d18e0e96/iK-POS-Left.png%3Fv%3D1666183791",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6eed9f060012d1d3a4/13de21e39192cb97818c1486f0911ebb/iK-POS-Top-Tilt.png%3Fv%3D1666183799",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6eb96e6a0012a853fa/1946469c593df671dcfc19bf0cc9174e/iK-POS-Top.png%3Fv%3D1666183799",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6ed4c98e0012ff70f2/045f0256fbaa6da5bc44fb30d0466b62/iK-POS-Iso-Left.png%3Fv%3D1666183791",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6fa2628c001253ece5/7a7930f604d389efe9353346dbee9fda/iK-POS-Iso-Right.png%3Fv%3D1666183791",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6f56d23a0012cef856/b7f2c536098e16039c2425624765aafb/iK-POS-Right.png%3Fv%3D1666183791",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6ee8f50d001194e664/375b3c8e0ac3af44f42bc40702ea34c6/iK-POS-Back.png%3Fv%3D1666183799",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6f56d23a0012cef859/a28067bf93ccd9a272adffc8aa4ede7a/iK-POS-Back-Tilt.png%3Fv%3D1666183799",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6f09550900128e0122/024bcee8be6acb0c13be0911a1dcc28f/iK-POS-Back-Right.png%3Fv%3D1666183799",
      //                             "https://cdn.swell.store/ikhokha_test/675a7d6f92fbff0012212d21/7c81858b8717deb696b73dfd238a7a4a/iK-POS-Back-Left.png%3Fv%3D1666183799"
      //                         ],
      //                         "quantity": 1,
      //                         "original_price": 9999,
      //                         "cart_metadata_fields": []
      //                     }
      //                 ],
      //                 "savings": 10599
      //             }
      //         }
      //     ],
      //     "shipping_address": {
      //         "first_name": "QUINCY",
      //         "last_name": "AMISSAH",
      //         "phone": "+233555599216",
      //         "address1": "48 Flower Street",
      //         "address2": null,
      //         "city": "Accra",
      //         "postal_code": "9300",
      //         "province": "Free State",
      //         "suburb": "Accra"
      //     },
      //     "invoice_address": {
      //         "first_name": null,
      //         "last_name": null,
      //         "phone": null,
      //         "address1": null,
      //         "address2": null,
      //         "city": null,
      //         "postal_code": null,
      //         "province": null,
      //         "suburb": null,
      //         "company_name": null,
      //         "vat_number": null
      //     },
      //     "contact": {
      //         "id": "68092cb8ab9b550012f728b7",
      //         "first_name": "QUINCY",
      //         "last_name": "AMISSAH",
      //         "email": "qhutchison8@gmail.com",
      //         "phone": "+233555599216",
      //         "addresses": []
      //     },
      //     "created_at": "2025-05-04T20:15:16.226Z",
      //     "updated_at": "2025-05-04T21:15:20.644Z",
      //     "fulfilment_mode": "delivery",
      //     "sales_agent": {
      //         "id": " ",
      //         "name": " "
      //     },
      //     "product_serial_numbers": [],
      //     "order_id": null
      // }
      // Transform to match internal structure
      const cartData = mapServerCartToInternal(retrievedCart);

      dispatch({
        type: 'SET_CART',
        payload: cartData,
      });

      setIsLoading(false);
      return cartData;
    } catch (error) {
      console.error('Error fetching cart:', error);
      setIsLoading(false);
      return null;
    }
  };

  const validateAddressFields = (
    address: CartStateType['deliveryAddress'] | CartStateType['billingAddress']
  ): boolean => {
    const requiredFields = [
      'first_name',
      'last_name',
      'phone',
      'address1',
      'province',
      'city',
      'postal_code',
      'suburb',
    ];

    return requiredFields.every(
      (field) => address && !!address[field as keyof typeof address]
    );
  };

  // Prepare cart for checkout
  const mapInternalCartToServerCart =
    async (): Promise<SalesCartModelPayload> => {
      const cart = computedCart;
      let contact: any = null;
      // validate all fields in contact are not falsy and  cart.billingAddress exists
      if (
        cart.contact &&
        Object.values(cart.contact).every((value) => !!value) &&
        cart.deliveryAddress &&
        cart.billingAddress
      ) {
        const first_name = cart.contact?.fullName.split(' ')[0];
        const last_name = cart.contact?.fullName.split(' ')[1];
        const contactPayload: any = {
          first_name,
          last_name,
          email: cart.contact?.email,
          phone: cart.contact?.cellNumber,
          addresses: [
            {
              ...cart.deliveryAddress,
              first_name,
              last_name,
              phone: cart.contact?.cellNumber,
            },
          ],
        };

        const { data, error } =
          await fetchOrCreateServerContact(contactPayload);
        if (error) {
          console.error('Error fetching or creating contact:', error);
          throw error;
        }

        if (!data) {
          console.error('No data returned from contact creation');
          throw 'No data returned from contact creation';
        }

        contact = {
          id: data.id,
        };

        cart.billingAddress = {
          ...cart.deliveryAddress,
          first_name,
          last_name,
          phone: cart.contact?.cellNumber,
        };

        cart.deliveryAddress = {
          ...cart.deliveryAddress,
          first_name,
          last_name,
          phone: cart.contact?.cellNumber,
        };
      }

      let lineItems = computedCart.lineItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        parent_id: item?.product?.variantGid ? item?.product?.productId : null,
        images: Array.isArray(item?.product?.images)
          ? item?.product?.images
              ?.map((img) => img.url)
              .filter((img): img is string => !!img)
          : item?.product?.images?.url
            ? [item?.product?.images.url]
            : [],
        sku: item?.product?.sku || '',
      }));

      const debitCardProduct = await debitCard();
      const hasDebitCard = lineItems.some(
        (item) => debitCardProduct && item.id === debitCardProduct.id
      );

      const isSoleProprietorWithCardMachine =
        cart.isSoleProprietor && cart.hasCardMachine;

      // Adding Debit Card if conditions are met
      if (
        isSoleProprietorWithCardMachine &&
        !hasDebitCard &&
        debitCardProduct
      ) {
        const images = Array.isArray(debitCardProduct?.images)
          ? debitCardProduct.images
              .map((img) => img.url)
              .filter((url): url is string => !!url)
          : debitCardProduct?.images?.url
            ? [debitCardProduct.images.url]
            : [];

        lineItems.push({
          id: debitCardProduct?.id,
          quantity: 1,
          parent_id: null,
          images,
          sku: debitCardProduct?.sku || '',
        });

        // Set product in store
        setProducts((prev) => ({
          ...prev,
          [debitCardProduct.id]: debitCardProduct,
        }));
      } else if (
        (!cart.isSoleProprietor || !cart.hasCardMachine) &&
        hasDebitCard &&
        lineItems.length > 1 // Ensure the cart has more than just the debit card(for purchase-debit card flow)
      ) {
        // Remove Debit Card if conditions are not met
        lineItems = lineItems.filter(
          (item) => item.id !== debitCardProduct?.id
        );
      }

      console.log({
        hasDebitCard,
        isSoleProprietorWithCardMachine,
        lineItems,
        debitCardProduct,
        cart,
      });

      const salesCartModel: any = {
        id: cart.id || '',
        ...(contact && { contact }),
        items: lineItems,
        product_serial_numbers: [],
        fulfilment_mode: SalesFulfilmentMode.Delivery,
        sales_channel: SalesChannel.SelfService,
        sales_agent: null,
        discount_codes: cart.discountCode ? [cart.discountCode] : [],

        ...(validateAddressFields(cart.billingAddress) && {
          invoice_address: {
            ...cart.billingAddress,
            vat_number: null,
            company_name: null,
          },
        }),
        ...(validateAddressFields(cart.deliveryAddress) && {
          shipping_address: cart.deliveryAddress,
        }),
      };
      console.log('Preparing cart for checkout:', salesCartModel);
      return salesCartModel;
    };

  //map server cart to internal cart
  const mapServerCartToInternal = (
    serverCart: SalesCartModel
  ): CartStateType => {
    let contact = cart.contact;
    let billingAddress = cart.billingAddress;
    let deliveryAddress = cart.deliveryAddress;
    if (serverCart?.contact?.email) {
      contact = {
        email: serverCart.contact?.email || '',
        cellNumber: serverCart.contact?.phone || '',
        fullName: `${serverCart.contact?.first_name || ''} ${
          serverCart.contact?.last_name || ''
        }`.trim(),
      };
    }

    if (serverCart?.shipping_address?.address1) {
      deliveryAddress = serverCart.shipping_address;
    }
    if (serverCart?.invoice_address?.address1) {
      billingAddress = serverCart.invoice_address;
    }

    const cartData: CartStateType = {
      id: serverCart.id || null,
      paymentRef: serverCart.payment_ref,
      lineItems: serverCart.items.map((item: any) => ({
        id: item.id,
        ...(item.product && { product: item.product }),
        quantity: item.quantity,
        lockQuantity: item.name.toLowerCase().includes('debit card'),
        hideLineItem:
          item.name.toLowerCase().includes('debit card') &&
          serverCart.items.length > 1,
      })),
      totalPrice: serverCart.total,
      totalTax: serverCart.vat,
      subtotalPrice: serverCart.total - serverCart.vat - serverCart.delivery,
      deliveryPrice: serverCart.delivery || 0,
      discountAmount: serverCart.discount_total || 0,
      discountCode: serverCart?.discount_codes?.length
        ? serverCart.discount_codes[0]
        : '',
      contact,
      deliveryAddress: deliveryAddress || {},
      billingAddress: billingAddress || {},
      billingIsSameAsDelivery:
        billingAddress?.address1 === deliveryAddress?.address1,
      isSoleProprietor: serverCart.items.some((item: any) =>
        item.name.toLowerCase().includes('debit card')
      ),
      hasCardMachine: cart.hasCardMachine,
      hasLocalChanges: false,
    };

    return cartData;
  };

  const createCart = async (): Promise<CartStateType | undefined> => {
    setIsLoading(true);

    if (isLoading) {
      return undefined;
    }
    try {
      const payload = await mapInternalCartToServerCart();
      const { data, error } = await createServerCart(payload);

      if (error) {
        console.error('Error creating cart:', error);
        throw error;
      }

      if (!data) {
        console.error('No data returned from cart creation');
        throw 'No data returned from cart creation';
      }

      const cartData = mapServerCartToInternal(data);

      dispatch({
        type: 'SET_CART',
        payload: cartData,
      });

      console.log({
        data,
        error,
      });
      return cartData;
    } catch (err) {
      console.error('Unexpected error while creating cart:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  //updateCart
  const updateCart = async (): Promise<CartStateType | undefined> => {
    setIsLoading(true);
    if (isLoading) {
      return undefined;
    }
    try {
      const payload = await mapInternalCartToServerCart();
      const { data, error } = await updateServerCart(payload.id, payload);

      console.log('payload', payload);
      if (error) {
        console.error('Error updating cart:', error);
        throw error;
      }
      if (!data) {
        console.error('No data returned from cart update');
        throw 'No data returned from cart update';
      }

      console.log({
        data,
      });
      const cartData = mapServerCartToInternal(data);
      console.log('updated cart ', cartData);
      dispatch({
        type: 'SET_CART',
        payload: cartData,
      });

      return cartData;
    } catch (err) {
      console.error('Unexpected error while updating cart:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const checkout = async (): Promise<{
    cart: SalesCartModel;
    paymentUrl: string;
  }> => {
    const cart = await updateCart();
    console.log('checkout cart', cart);

    if (!cart || !cart?.paymentRef) {
      console.error('Cart is empty or payment reference is missing');
      throw 'Cart is empty or payment reference is missing';
    }
    const { data, error } = await generateServerPaymentUrl(cart.paymentRef);
    console.log('checkout', data, error);
    if (error) {
      console.error('Error preparing cart for checkout:', error);
      throw error;
    }
    if (!data) {
      console.error('No data returned from cart preparation');
      throw 'No data returned from cart preparation';
    }

    const cartDataMapped = mapServerCartToInternal(data.cart);
    console.log('checkout cart data', cartDataMapped);
    dispatch({
      type: 'SET_CART',
      payload: cartDataMapped,
    });
    return data;
  };

  const formatAndSetInitialCart = (
    cart: SalesCartModel & { products: { [id: string]: ProductType } },
    source: 'default' | 'ik-card'
  ): void => {
    console.log('formatAndSetInitialCart', cart, source);
    let cartDataMapped: CartStateType = mapServerCartToInternal(cart);

    console.log({ cartDataMapped });
    const debitCard = Object.values(cart.products).find(
      (product) =>
        product.slug === PurchaseJourneyConstants.DEBIT_CARD_PRODUCT_ID
    );
    const debitCardLineItem = cartDataMapped.lineItems.find(
      (item) => debitCard && item.id === debitCard?.id
    );

    if (source === 'ik-card') {
      if (debitCard && debitCardLineItem) {
        debitCardLineItem.lockQuantity = true;
        debitCardLineItem.hideLineItem = false;
      }
    } else {
      if (debitCard && debitCardLineItem) {
        debitCardLineItem.lockQuantity = true;
        debitCardLineItem.hideLineItem = true;
      }
    }

    setProducts(cart.products);
    dispatch({
      type: 'SET_CART',
      payload: cartDataMapped,
    });
  };
  return {
    // Core cart state
    cart: computedCart,
    items,
    totals,
    itemCount,
    isEmpty: items.length === 0,
    isCartOpen,

    // Core operations
    addItem,
    removeItem,
    getCart,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    applyDiscount,
    clearDiscount,
    setDiscount,
    clearCart,
    setCart,
    updateContact,
    updateDeliveryAddress,
    updateBillingAddress,
    updateBillingSameAsDelivery,
    updateSoleProprietor,
    // Cart UI
    openCart,
    closeCart,
    toggleCart,

    // Loading states
    isLoading,
    setIsLoading,
    isRemovingItem,
    setIsRemovingItem,
    isAddingItem,
    setIsAddingItem,
    isRemovingLineItem,
    setIsRemovingLineItem,

    // External integrations
    mixpanelUserId,
    setMixpanelUserId,

    // Cart ID
    cartId,
    setCartId,
    formatAndSetInitialCart,

    createCart,
    updateCart,
    getDebitCard,
    checkout,
  };
};

export default useCart;
